import { useRender } from '@base-ui/react/use-render';
import { useRef, type ReactElement } from 'react';
import { cn } from '../util/classes.ts';
import { useStuck } from '../util/use-stuck.ts';
import { Container, type ContainerProps } from './container.tsx';

export type SectionCardProps = ContainerProps & {
    /** Card sits on white rather than the page wash: drop the elevation, keep the padding. */
    flat?: boolean;
};

/**
 * SectionCard — the large titled surface a page section sits on, composed from
 * slots like `Card`.
 *
 * Elevation, not outline: the resting-card shadow carries the card off the page
 * wash, so there is deliberately no border.
 *
 * It resolves to container level 1, so `Card` children land at level 2 and keep
 * their own box. For rows without a box of their own (a feed grouped by
 * customer), wrap them in a `Container` to push them to level 3.
 *
 * @example
 * <SectionCard>
 *     <SectionCardHeader>
 *         <SectionCardTitle>Anstehend</SectionCardTitle>
 *         <SectionCardAction>Alle Termine</SectionCardAction>
 *     </SectionCardHeader>
 *     <Card>…</Card>
 * </SectionCard>
 */
export function SectionCard({
    className,
    flat = false,
    minLevel = 1,
    nestLevel = 0,
    ...props
}: SectionCardProps): ReactElement {
    return (
        <Container
            data-slot="section-card"
            minLevel={minLevel}
            nestLevel={nestLevel}
            className={cn(
                // gap-cozy, not the full container rung: nothing draws a line
                // under the title, so a wider gap reads as a hole
                'flex flex-col gap-cozy rounded-2xl bg-container p-container',
                // clip, not hidden: no scroll container, so a sticky header still pins to the page
                // and is cut to the bottom corners when the card end pushes it out
                'overflow-clip',
                !flat && 'shadow-xs',
                className,
            )}
            {...props}
        />
    );
}

export type SectionCardHeaderProps = useRender.ComponentProps<'div'>;

export function SectionCardHeader({ className, render, ...props }: SectionCardHeaderProps): ReactElement {
    return useRender({
        defaultTagName: 'div',
        render,
        props: {
            'data-slot': 'section-card-header',
            ...props,
            className: cn('flex items-center justify-between gap-cozy', className),
        },
    });
}

export type SectionCardStickyHeaderProps = SectionCardHeaderProps;

/**
 * A header that sticks while the card's body scrolls beneath it, with a fold shadow once stuck.
 * Offset: `--section-card-sticky-top`; corner fill: `--section-card-sticky-bg` (page wash);
 * gap above it once stuck: `--section-card-sticky-gap` (0 under a sticky bar that pads its own).
 */
export function SectionCardStickyHeader({ className, ...props }: SectionCardStickyHeaderProps): ReactElement {
    const ref = useRef<HTMLDivElement>(null);
    const stuck = useStuck(ref);

    return (
        <div
            ref={ref}
            className={cn(
                'sticky top-(--section-card-sticky-top,0px) z-2',
                // the gap strip sits above the card at rest, where the card's overflow-clip hides it
                '-mx-container -mt-[calc(var(--spacing-container)+var(--section-card-sticky-gap,var(--spacing-cozy)))] -mb-cozy',
                'pt-(--section-card-sticky-gap,var(--spacing-cozy))',
                'bg-(--section-card-sticky-bg,var(--color-wash))',
            )}
        >
            <SectionCardHeader
                className={cn(
                    'rounded-t-2xl bg-container px-container pt-container pb-cozy',
                    'transition-shadow',
                    stuck && 'shadow-fold-down',
                    className,
                )}
                {...props}
            />
        </div>
    );
}

export type SectionCardTitleProps = useRender.ComponentProps<'h3'>;

export function SectionCardTitle({ className, render, ...props }: SectionCardTitleProps): ReactElement {
    return useRender({
        defaultTagName: 'h3',
        render,
        props: {
            'data-slot': 'section-card-title',
            ...props,
            className: cn('min-w-0 truncate text-card-heading', className),
        },
    });
}

export type SectionCardGroupProps = useRender.ComponentProps<'section'>;

/**
 * A labelled group inside the card's body. A card can hold several — "Geplant"
 * carries Nächster Termin, Letzter Termin and Offene Aufgaben — which is why
 * the label lives here and not in the header.
 */
export function SectionCardGroup({ className, render, ...props }: SectionCardGroupProps): ReactElement {
    return useRender({
        defaultTagName: 'section',
        render,
        props: {
            'data-slot': 'section-card-group',
            ...props,
            className: cn('flex flex-col gap-cozy', className),
        },
    });
}

export type SectionCardGroupTitleProps = useRender.ComponentProps<'h4'>;

/**
 * The group's quiet caption — a demoted title that labels the group without
 * competing with the card's own. The content explains what it is.
 */
export function SectionCardGroupTitle({
    className,
    render,
    ...props
}: SectionCardGroupTitleProps): ReactElement {
    return useRender({
        defaultTagName: 'h4',
        render,
        props: {
            'data-slot': 'section-card-group-title',
            ...props,
            className: cn('text-subtle-heading', className),
        },
    });
}

export type SectionCardActionProps = useRender.ComponentProps<'div'>;

/**
 * The header's trailing slot — a way-in link, a toggle, a menu trigger. It owns
 * only the quiet typography and the row layout; render whatever the card needs
 * inside, including a bare icon, which sizes itself.
 *
 * A way-in hint stays quiet rather than taking the accent colour, which is
 * reserved for navigation and real actions.
 */
export function SectionCardAction({ className, render, ...props }: SectionCardActionProps): ReactElement {
    return useRender({
        defaultTagName: 'div',
        render,
        props: {
            'data-slot': 'section-card-action',
            ...props,
            className: cn(
                'flex shrink-0 items-center gap-tight text-caption text-content-weaker',
                "*:[svg:not([class*='size-'])]:size-3",
                className,
            ),
        },
    });
}
